import { useTranslation } from "react-i18next";
import { useCallStateHooks, useCall } from "@stream-io/video-react-sdk";
import { RecordingButton } from "@/features/call/components/RecordingButton";
import { ScreenShareIcon } from "@/features/call/components/ScreenShareIndicator";
import { useLocalScreenShare } from "@/features/call/hooks/useScreenShare";
import { useDeviceAction } from "@/features/call/hooks/useDeviceAction";

interface CallControlsProps {
  onLeave: () => void;
  onEndForAll?: () => void;
  onMinimize: () => void;
  isGroupCall?: boolean;
  /** Only the call initiator may share their screen */
  canScreenShare?: boolean;
  /** Creator's current lock state (all mics locked) */
  allMicsMuted?: boolean;
  /** Callback for creator to toggle the lock */
  onToggleMuteLock?: () => void;
  /** Whether this participant has been mute-locked by the creator */
  isLockedByCreator?: boolean;
}

export const CallControls = ({
  onLeave,
  onEndForAll,
  onMinimize,
  isGroupCall,
  canScreenShare = false,
  allMicsMuted = false,
  onToggleMuteLock,
  isLockedByCreator = false,
}: CallControlsProps) => {
  const { t } = useTranslation();
  const { useMicrophoneState, useCameraState, useScreenShareState } =
    useCallStateHooks();
  const { microphone, isMute: isMicMuted } = useMicrophoneState();
  const { camera, isMute: isCameraOff } = useCameraState();
  const { isMute: isScreenShareOff } = useScreenShareState();
  const { sourceLabel, toggle: toggleScreenShare, isToggling: isScreenShareToggling } =
    useLocalScreenShare();
  const call = useCall();

  // STRICT EXCLUSIVITY GATES: Only one of these can ever be true
  const isGroupCreator = isGroupCall && !!onEndForAll;
  const isGroupMember = isGroupCall && !onEndForAll;
  const isOneOnOne = !isGroupCall;

  // Device toggles reject when the browser blocks the device (permission
  // denied, no device, device in use). Route them through useDeviceAction so
  // the failure becomes a toast rather than an unhandled rejection, and so a
  // second click while the first is still in flight is ignored.
  const mic = useDeviceAction("microphone", () => microphone.toggle());
  const cam = useDeviceAction("camera", () => camera.toggle());

  const raiseHand = () => {
    call?.sendCustomEvent({ type: "raise-hand", data: {} });
  };

  return (
    <div className="flex items-center justify-center gap-3 pb-6 pt-2">
      {/* Mute Button */}
      <button
        onClick={() => !isLockedByCreator && mic.run()}
        disabled={isLockedByCreator || mic.busy}
        aria-busy={mic.busy}
        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all relative ${
          isLockedByCreator
            ? "bg-red-500/20 text-red-400 cursor-not-allowed opacity-60"
            : isMicMuted
            ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
            : "bg-white/10 text-white hover:bg-white/20"
        }`}
        title={isLockedByCreator ? t("call.muted_by_host") : isMicMuted ? t("call.unmute") : t("call.mute")}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          {/* Same microphone glyph in both states — struck through when muted,
              so the button always reads as "microphone". */}
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
          />
          {isMicMuted && (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4l16 16"
            />
          )}
        </svg>
        {isLockedByCreator && (
          <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-red-600 flex items-center justify-center">
            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 1a5 5 0 00-5 5v3H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V11a2 2 0 00-2-2h-2V6a5 5 0 00-5-5zm-3 5a3 3 0 016 0v3H9V6zm3 9a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </span>
        )}
      </button>

      {/* Camera Button */}
      <button
        onClick={cam.run}
        disabled={cam.busy}
        aria-busy={cam.busy}
        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
          isCameraOff
            ? "bg-white/10 text-white hover:bg-white/20"
            : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
        }`}
        title={isCameraOff ? t("call.turn_camera_on") : t("call.turn_camera_off")}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          {/* Always a camera — struck through when the camera is off, so the
              button that turns it back on still looks like a camera. */}
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
          {isCameraOff && (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4l16 16"
            />
          )}
        </svg>
      </button>

      {/* Screen Share Button — visible to call initiator only */}
      {canScreenShare && (
        <button
          onClick={toggleScreenShare}
          disabled={isScreenShareToggling}
          aria-busy={isScreenShareToggling}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
            !isScreenShareOff
              ? "bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30"
              : "bg-white/10 text-white hover:bg-white/20"
          }`}
          title={
            !isScreenShareOff
              ? `${t("call.stop_sharing")} — ${sourceLabel}`
              : t("call.share_screen")
          }
        >
          <ScreenShareIcon />
        </button>
      )}

      {/* Recording Button — group creator only */}
      {isGroupCreator && <RecordingButton />}

      {/* Lock Mics Button — group creator only */}
      {isGroupCreator && onToggleMuteLock && (
        <button
          onClick={onToggleMuteLock}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
            allMicsMuted
              ? "bg-red-500/40 text-red-300 hover:bg-red-500/50"
              : "bg-white/10 text-white hover:bg-white/20"
          }`}
          title={allMicsMuted ? t("call.unlock_mics") : t("call.lock_all_mics")}
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            {allMicsMuted ? (
              <path d="M12 1a5 5 0 00-5 5v3H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V11a2 2 0 00-2-2h-2V6a5 5 0 00-5-5zm-3 5a3 3 0 016 0v3H9V6zm3 9a2 2 0 110-4 2 2 0 010 4z" />
            ) : (
              <path d="M17 9V6a5 5 0 00-10 0v3H5a2 2 0 00-2 2v10a2 2 0 002 2h14a2 2 0 002-2V11a2 2 0 00-2-2h-2zm-5 9a2 2 0 110-4 2 2 0 010 4zM9 9V6a3 3 0 016 0v3H9z" />
            )}
          </svg>
        </button>
      )}

      {/* Raise Hand Button */}
      <button
        onClick={raiseHand}
        className="w-12 h-12 rounded-full bg-white/10 text-white hover:bg-amber-500/20 hover:text-amber-400 flex items-center justify-center transition-all"
        title={t("call.raise_hand")}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11"
          />
        </svg>
      </button>

      {/* Minimize Button */}
      <button
        onClick={onMinimize}
        className="w-12 h-12 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center transition-all"
        title={t("call.minimize_call")}
      >
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </button>

      {/* ─── ISOLATED CALL ACTION BUTTONS ─── */}

      {/* 1. CREATOR ONLY: Red "End" */}
      {isGroupCreator && (
        <button
          onClick={onEndForAll}
          className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors"
          title={t("call.end_call_for_everyone")}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.516l2.257-1.13a1 1 0 00.502-1.21L8.23 3.684A1 1 0 007.28 3H5z"
            />
          </svg>
        </button>
      )}

      {/* 2. MEMBER ONLY: Orange "Leave" */}
      {isGroupMember && (
        <button
          onClick={onLeave}
          className="w-12 h-12 rounded-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-white transition-colors"
          title={t("call.leave_call")}
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
        </button>
      )}

      {/* 3. 1:1 CALL ONLY: Red "End" */}
      {isOneOnOne && (
        <button
          onClick={onLeave}
          className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors"
          title={t("call.end_call")}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.516l2.257-1.13a1 1 0 00.502-1.21L8.23 3.684A1 1 0 007.28 3H5z"
            />
          </svg>
        </button>
      )}
    </div>
  );
};
